import { isDefined, isNil } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api/constants";
import { helloSignClient } from "@src/appV2/HelloSign/helloSignClient";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isAxiosError } from "axios";
import { useCallback, useRef, useState } from "react";

import {
  HelloSignErrorCodes,
  HelloSignErrorReason,
  HelloSignWorkflowSessionStatus,
} from "./constants";
import { useCreateHelloSignSession } from "./useCreateHelloSignSession";
import { useHelloSignClientEvents } from "./useHelloSignClientEvents";
import { useHelloSignHandleSuccessOrError } from "./useHelloSignSuccessOrError";
import { useUpdateHelloSignSession } from "./useUpdateHelloSignSession";

interface UseHelloSignEmbeddedFlowProps {
  requirementId: string;
  compositeChildRequirementId?: string;
  // We need a container as it doesn't automatically handle safe areas on phones
  helloSignIframeContainerRef: React.RefObject<HTMLDivElement>;
  onHelloSignClientCancel: () => void;
  onSuccess: (isDocumentApproved: boolean) => void;
  onError: () => void;
  enabled?: boolean;
}

export function useHelloSignEmbeddedFlow(props: UseHelloSignEmbeddedFlowProps) {
  const {
    requirementId,
    compositeChildRequirementId,
    helloSignIframeContainerRef,
    onHelloSignClientCancel,
    onSuccess,
    onError,
    enabled = true,
  } = props;

  const [helloSignSessionId, setHelloSignSessionId] = useState<string | undefined>(undefined);
  const helloSignOpenStartTime = useRef<number | undefined>(undefined);

  const worker = useDefinedWorker();
  const { mutateAsync: createHelloSignSession, isLoading: isCreatingHelloSignSession } =
    useCreateHelloSignSession({
      requirementId,
      workerId: worker.userId,
    });

  const {
    mutateAsync: updateHelloSignSessionToSigned,
    isLoading: isUpdatingHelloSignSessionToSigned,
  } = useUpdateHelloSignSession({
    helloSignSessionId,
    requirementId,
    workerId: worker.userId,
    include: API_TYPES.documents,
  });

  const { mutateAsync: updateHelloSignSessionToErrored } = useUpdateHelloSignSession({
    helloSignSessionId,
    requirementId,
    workerId: worker.userId,
  });

  const { handleHelloSignSuccess, handleHelloSignError } = useHelloSignHandleSuccessOrError({
    requirementId,
    onSuccess,
    onError,
  });

  const startHelloSignEmbeddedFlow = useCallback(async () => {
    if (!enabled) {
      return;
    }

    const logMetadata: { helloSignSessionId?: string } = {};

    try {
      if (isNil(requirementId)) {
        throw new Error("Missing requirementId");
      }

      const session = await createHelloSignSession({
        data: {
          type: API_TYPES.helloSignSession,
          ...(isDefined(compositeChildRequirementId) && {
            attributes: {
              childRequirementId: compositeChildRequirementId,
            },
          }),
        },
      });

      logMetadata.helloSignSessionId = session.data.id;
      setHelloSignSessionId(session.data.id);

      if (isNil(session.data.attributes.signUrl)) {
        throw new Error("Missing signUrl");
      }

      const { signUrl } = session.data.attributes;
      logEvent(APP_V2_APP_EVENTS.HELLO_SIGN_SESSION_STARTED, {
        requirementId,
        signUrl,
        helloSignSessionId: session.data.id,
      });
      helloSignOpenStartTime.current = Date.now();
      helloSignClient.open(signUrl, {
        skipDomainVerification: true,
        container: isDefined(helloSignIframeContainerRef.current)
          ? helloSignIframeContainerRef.current
          : undefined,
      });
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        handleHelloSignError(HelloSignErrorReason.ALREADY_SIGNED, logMetadata.helloSignSessionId);
      } else {
        handleHelloSignError(HelloSignErrorReason.BACKEND_ERROR, logMetadata.helloSignSessionId);
      }
    }
  }, [
    enabled,
    requirementId,
    createHelloSignSession,
    compositeChildRequirementId,
    helloSignIframeContainerRef,
    handleHelloSignError,
  ]);

  useHelloSignClientEvents({
    enabled,
    onSign: async () => {
      let attempts = 0;
      const maxAttempts = 3;
      const delayTime = 1000;
      const delay = async (ms: number) =>
        await new Promise((resolve) => {
          setTimeout(resolve, ms);
        });

      /*
       * We want to retry this request a few times, as its possible the backend
       * will be having a write conflict with the running webhook which is currently
       * processing the resource
       */
      while (attempts < maxAttempts) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const helloSignSession = await updateHelloSignSessionToSigned({
            status: HelloSignWorkflowSessionStatus.SIGNED,
          });
          logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_SUCCESS, {
            requirement_id: requirementId,
          });
          handleHelloSignSuccess(helloSignSession);
          return;
        } catch {
          attempts += 1;
          if (attempts < maxAttempts) {
            // eslint-disable-next-line no-await-in-loop
            await delay(delayTime);
          } else {
            handleHelloSignError(HelloSignErrorReason.BACKEND_ERROR);
          }
        }
      }
    },
    onError: async (errorCode) => {
      if (errorCode !== HelloSignErrorCodes.NOTHING_TO_SIGN) {
        await updateHelloSignSessionToErrored({
          status: HelloSignWorkflowSessionStatus.ERRORED,
          errorMessage: errorCode,
        });
        logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_FAILURE, {
          requirement_id: requirementId,
        });
        handleHelloSignError(HelloSignErrorReason.BACKEND_ERROR);
      }
    },
    onCancel: () => {
      onHelloSignClientCancel();
    },
    requirementId,
    helloSignSessionId,
    helloSignOpenStartTime: helloSignOpenStartTime.current,
  });

  return {
    isCreatingHelloSignSession,
    isSubmittingHelloSignDocument: isUpdatingHelloSignSessionToSigned,
    startHelloSignEmbeddedFlow,
  };
}
